import React, { useState, useRef } from 'react';
import Cropper from 'react-cropper';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import './ImageCropper.css';

/**
 * Componente para recortar imágenes con guías rojas
 * Usa Cropper.js para la funcionalidad de recorte
 */
const ImageCropper = ({ image, isOpen, toggle, onCropComplete }) => {
  const cropperRef = useRef(null);
  const [cropping, setCropping] = useState(false);

  const handleCrop = () => {
    setCropping(true);
    const cropper = cropperRef.current?.cropper;
    
    if (!cropper) {
      console.error('❌ No hay cropper disponible');
      setCropping(false);
      return;
    }
    
    try {
      // Obtener datos del recorte
      const cropData = cropper.getData();
      //console.log('📐 Datos del recorte:', cropData);
      
      // Validar que el recorte tenga dimensiones mínimas
      if (cropData.width < 200 || cropData.height < 200) {
        console.warn('⚠️ Recorte muy pequeño:', cropData);
        alert('El área recortada es muy pequeña. Por favor, selecciona un área más grande (mínimo 200x200 píxeles).');
        setCropping(false);
        return;
      }
      
      // Obtener canvas con opciones específicas
      // IMPORTANTE: Backend requiere mínimo 800x600px
      const canvas = cropper.getCroppedCanvas({
        minWidth: 800,   // Mínimo requerido por backend
        minHeight: 600,  // Mínimo requerido por backend
        maxWidth: 2400,
        maxHeight: 2400,
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });
      
      if (!canvas) {
        console.error('❌ No se pudo generar el canvas');
        alert('Error al procesar el recorte. Por favor, intenta nuevamente.');
        setCropping(false);
        return;
      }
      
      //console.log('✅ Canvas generado:', canvas.width, 'x', canvas.height);
      
      // Convertir canvas a blob con validación
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('❌ No se pudo generar el blob');
          alert('Error al procesar la imagen recortada. Por favor, intenta nuevamente.');
          setCropping(false);
          return;
        }
        
        //console.log('✅ Blob generado:', blob.size, 'bytes, tipo:', blob.type);
        
        // Validar tamaño del blob
        if (blob.size < 1000) {
          console.error('❌ Blob muy pequeño:', blob.size);
          alert('La imagen recortada es muy pequeña o está corrupta. Por favor, intenta nuevamente.');
          setCropping(false);
          return;
        }
        
        // Crear un File object del blob con nombre único
        const timestamp = Date.now();
        const file = new File([blob], `factura_recortada_${timestamp}.jpg`, {
          type: 'image/jpeg',
          lastModified: timestamp
        });
        
        //console.log('✅ Archivo creado:', file.name, file.size, 'bytes');
        
        setCropping(false);
        onCropComplete(file);
        toggle();
      }, 'image/jpeg', 0.92); // Calidad 92% para balance entre tamaño y calidad
      
    } catch (error) {
      console.error('❌ Error en handleCrop:', error);
      alert('Error al procesar el recorte. Por favor, intenta nuevamente.');
      setCropping(false);
    }
  };

  const handleCancel = () => {
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" className="modal-cropper">
      <ModalHeader toggle={toggle}>
        <i className="ri-crop-line me-2"></i>
        Recortar Imagen de Factura
      </ModalHeader>
      <ModalBody>
        <div className="cropper-container-wrapper">
          <Cropper
            ref={cropperRef}
            src={image}
            style={{ height: 500, width: '100%' }}
            // Configuración de guías
            guides={true}
            center={true}
            highlight={true}
            background={true}
            // Aspecto libre
            aspectRatio={NaN}
            viewMode={1}
            dragMode="move"
            // Zoom
            zoomable={true}
            zoomOnWheel={true}
            wheelZoomRatio={0.1}
            // Responsive
            responsive={true}
            restore={true}
            checkOrientation={true}
            // Estilos personalizados con guías rojas
            className="custom-cropper"
          />
        </div>
        <div className="mt-3">
          <div className="alert alert-info mb-0 py-2">
            <div className="d-flex align-items-start">
              <i className="ri-information-line me-2 mt-1"></i>
              <div className="small">
                <strong>Instrucciones:</strong>
                <ul className="mb-0 mt-1 ps-3">
                  <li>Arrastra para mover la imagen</li>
                  <li>Usa la rueda del mouse para hacer zoom</li>
                  <li>Ajusta el área de recorte con las esquinas</li>
                  <li><strong className="text-primary">Importante:</strong> Selecciona un área grande que incluya toda la información de la factura para mejores resultados</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel} disabled={cropping}>
          <i className="ri-close-line me-1"></i>
          Cancelar
        </Button>
        <Button color="success" onClick={handleCrop} disabled={cropping}>
          {cropping ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Procesando...
            </>
          ) : (
            <>
              <i className="ri-check-line me-1"></i>
              Aplicar Recorte
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ImageCropper;
